import type { ContactRattache, PersonneDetail } from '../types';
import { estMineur } from './age';

/**
 * ATTESTATION DE PAIEMENT (2026-10-31) — ce qui entoure le PDF : les
 * destinataires proposes depuis la fiche, et le courriel Gmail prerempli
 * qui l'accompagne. Premier temps, comme la relance : un lien de
 * composition Gmail ne porte pas de piece jointe, le PDF est telecharge
 * sur le poste et l'utilisateur le glisse dans le message. Le brouillon
 * avec piece jointe viendra avec l'API Gmail du lot 3.
 */

export interface DestinataireAttestation {
  email: string;
  /** Ce qui s'affiche a cote de l'adresse : « Fiche », « Mère — NOM Prénom »… */
  libelle: string;
  /** Coche par defaut : la fiche pour un adulte, les parents pour un mineur. */
  parDefaut: boolean;
}

const LIBELLE_QUALITE: Record<string, string> = {
  mere: 'Mère',
  pere: 'Père',
  tuteur: 'Tuteur',
  eleve: 'Élève',
  conjoint: 'Conjoint',
  autre: 'Autre',
};
const PARENTS = new Set(['mere', 'pere', 'tuteur']);

/**
 * Les adresses de la fiche et de ses contacts, dedoublonnees, avec leur
 * choix par defaut : l'adulte recoit lui-meme, les parents d'un mineur
 * recoivent pour lui — la regle du lien « Relancer ». Les contacts
 * d'un frere ou d'une soeur (affiches par derivation) sont proposes
 * aussi : c'est souvent la meme famille.
 */
export function destinatairesAttestation(personne: PersonneDetail): DestinataireAttestation[] {
  const mineur = estMineur(personne.date_naissance);
  const parentsDAbord = mineur !== false;
  const liste: DestinataireAttestation[] = [];
  const vus = new Set<string>();
  const ajouter = (email: string | null, libelle: string, parDefaut: boolean) => {
    const propre = email?.trim();
    if (!propre) return;
    const cle = propre.toLowerCase();
    if (vus.has(cle)) return;
    vus.add(cle);
    liste.push({ email: propre, libelle, parDefaut });
  };
  ajouter(personne.email, `Fiche — ${personne.nom.toUpperCase()} ${personne.prenom}`, !parentsDAbord);
  const contacts: ContactRattache[] = personne.contacts ?? [];
  contacts.forEach((c) => {
    const nom = [c.nom?.toUpperCase(), c.prenom].filter(Boolean).join(' ') || c.email || '';
    const via = c.via ? ` (contact de ${c.via.prenom})` : '';
    ajouter(c.email, `${LIBELLE_QUALITE[c.qualite] ?? c.qualite} — ${nom}${via}`, parentsDAbord && PARENTS.has(c.qualite) && !c.via);
  });
  // Un mineur sans parent joignable : la fiche elle-meme, s'il y en a une.
  if (parentsDAbord && !liste.some((d) => d.parDefaut) && liste[0]) liste[0].parDefaut = true;
  return liste;
}

export function sujetAttestation(ecole: string, prenom: string, nom: string, annee: string): string {
  return `${ecole} | Attestation de paiement ${prenom} ${nom.toUpperCase()} — ${annee}`;
}

export function corpsAttestation(raisonSociale: string, prenom: string, nom: string, annee: string): string {
  return [
    'Bonjour,',
    '',
    `Vous trouverez ci-joint l’attestation de paiement de ${prenom} ${nom.toUpperCase()} pour l’année scolaire ${annee}.`,
    '',
    'Nous restons à votre disposition pour toute question.',
    '',
    'Bien cordialement,',
    raisonSociale,
  ].join('\n');
}
